import React from 'react';

/* ------------------------------------------------------------------ */
/*  Shared look-and-feel for the Everee / Empivo agreement form        */
/*  (dark section bars, tan table headers, underlined fill-in fields)  */
/* ------------------------------------------------------------------ */

export const BORDER = 'border-[#cfcac2]';
export const TAN = 'bg-[#e6e3de]';
export const TAN_LIGHT = 'bg-[#f4f2ee]';

export const getError = (errors, name) =>
  name.split('.').reduce((o, k) => (o ? o[k] : undefined), errors);

/* ----------------------------- masks ------------------------------ */
const digits = (v) => (v || '').replace(/\D/g, '');

export const MASKS = {
  int: (v) => digits(v).slice(0, 7),
  ein: (v) => {
    const d = digits(v).slice(0, 9);
    return d.length > 2 ? `${d.slice(0, 2)}-${d.slice(2)}` : d;
  },
  ssn: (v) => {
    const d = digits(v).slice(0, 9);
    if (d.length > 5) return `${d.slice(0, 3)}-${d.slice(3, 5)}-${d.slice(5)}`;
    if (d.length > 3) return `${d.slice(0, 3)}-${d.slice(3)}`;
    return d;
  },
  phone: (v) => {
    const d = digits(v).slice(0, 10);
    if (d.length > 6) return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
    if (d.length > 3) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
    return d.length ? `(${d}` : '';
  },
  zip: (v) => {
    const d = digits(v).slice(0, 9);
    return d.length > 5 ? `${d.slice(0, 5)}-${d.slice(5)}` : d;
  },
  dob: (v) => {
    const d = digits(v).slice(0, 8);
    if (d.length > 4) return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`;
    if (d.length > 2) return `${d.slice(0, 2)}/${d.slice(2)}`;
    return d;
  },
  percent: (v) => (v || '').replace(/[^\d.]/g, '').slice(0, 6),
  money: (v) => {
    const d = digits(v).slice(0, 12);
    return d ? `$${Number(d).toLocaleString('en-US')}` : '';
  },
  aba: (v) => digits(v).slice(0, 9),
  initials: (v) => (v || '').replace(/[^a-zA-Z]/g, '').toUpperCase().slice(0, 4),
};

/* --------------------------- validators --------------------------- */
export const REQ = 'Required';
export const optional = (fn) => (v) => !v || fn(v);

export const V = {
  ein: (v) => /^\d{2}-\d{7}$/.test(v || '') || 'Format: XX-XXXXXXX',
  ssn: (v) => /^\d{3}-\d{2}-\d{4}$/.test(v || '') || 'Format: XXX-XX-XXXX',
  phone: (v) => digits(v).length === 10 || 'Enter a 10-digit phone number',
  zip: (v) => /^\d{5}(-\d{4})?$/.test(v || '') || 'Invalid ZIP code',
  email: (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v || '') || 'Invalid email address',
  aba: (v) => digits(v).length === 9 || 'Must be 9 digits',
  initials: (v) => (v || '').length >= 2 || 'Enter your initials',
  percent: (v) => {
    const n = parseFloat(v);
    return (n > 0 && n <= 100) || 'Enter 0 – 100';
  },
  dob: (v) => {
    const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(v || '');
    if (!m) return 'Use MM/DD/YYYY';
    const [mo, d, y] = [m[1], m[2], m[3]].map(Number);
    const dt = new Date(y, mo - 1, d);
    if (dt.getFullYear() !== y || dt.getMonth() !== mo - 1 || dt.getDate() !== d || y < 1900) {
      return 'Invalid date';
    }
    if (new Date(y + 18, mo - 1, d) > new Date()) return 'Must be 18 or older';
    return true;
  },
};

/* ------------------------------ data ------------------------------ */
// Alphabetical – with grid-flow-col / 10 rows this reproduces the 5 columns of the Word form
export const US_STATES = [
  ['AL', 'Alabama'], ['AK', 'Alaska'], ['AZ', 'Arizona'], ['AR', 'Arkansas'], ['CA', 'California'],
  ['CO', 'Colorado'], ['CT', 'Connecticut'], ['DE', 'Delaware'], ['FL', 'Florida'], ['GA', 'Georgia'],
  ['HI', 'Hawaii'], ['ID', 'Idaho'], ['IL', 'Illinois'], ['IN', 'Indiana'], ['IA', 'Iowa'],
  ['KS', 'Kansas'], ['KY', 'Kentucky'], ['LA', 'Louisiana'], ['ME', 'Maine'], ['MD', 'Maryland'],
  ['MA', 'Massachusetts'], ['MI', 'Michigan'], ['MN', 'Minnesota'], ['MS', 'Mississippi'], ['MO', 'Missouri'],
  ['MT', 'Montana'], ['NE', 'Nebraska'], ['NV', 'Nevada'], ['NH', 'New Hampshire'], ['NJ', 'New Jersey'],
  ['NM', 'New Mexico'], ['NY', 'New York'], ['NC', 'North Carolina'], ['ND', 'North Dakota'], ['OH', 'Ohio'],
  ['OK', 'Oklahoma'], ['OR', 'Oregon'], ['PA', 'Pennsylvania'], ['RI', 'Rhode Island'], ['SC', 'South Carolina'],
  ['SD', 'South Dakota'], ['TN', 'Tennessee'], ['TX', 'Texas'], ['UT', 'Utah'], ['VT', 'Vermont'],
  ['VA', 'Virginia'], ['WA', 'Washington'], ['WV', 'West Virginia'], ['WI', 'Wisconsin'], ['WY', 'Wyoming'],
];

export const today = () => new Date().toISOString().slice(0, 10);

/* --------------------------- layout bits -------------------------- */
export const DocTitle = ({ children, subtitle }) => (
  <div className="mb-6 text-center">
    <h2 className="text-lg font-bold uppercase tracking-[0.18em] text-gray-900 md:text-xl">{children}</h2>
    {subtitle && <p className="mt-1 text-sm text-gray-500">{subtitle}</p>}
    <div className="mx-auto mt-3 h-0.5 w-16 bg-blue-700" />
  </div>
);

export const SectionBar = ({ children, className = '' }) => (
  <div
    className={`bg-[#404040] px-4 py-2 text-center text-xs font-bold uppercase tracking-[0.2em] text-white ${className}`}
  >
    {children}
  </div>
);

export const Panel = ({ children, className = '' }) => (
  <div className={`border ${BORDER} ${className}`}>{children}</div>
);

/* ------------------------------ fields ---------------------------- */
const controlClass = (layout, hasError) => {
  const base =
    'w-full bg-transparent text-sm text-gray-900 placeholder-gray-300 outline-none transition-colors read-only:bg-gray-50 read-only:text-gray-400 focus:ring-0';
  if (layout === 'row') {
    return `${base} border-0 px-1 py-1.5 focus:bg-blue-50/60 ${hasError ? 'bg-red-50/60' : ''}`;
  }
  return `${base} border-0 border-b px-1 py-1.5 focus:bg-blue-50/60 focus:border-blue-600 ${
    hasError ? 'border-red-500 bg-red-50/50' : 'border-gray-400'
  }`;
};

const FieldShell = ({ layout, id, label, required, error, className = '', children }) => {
  const msg = error ? error.message || REQ : null;
  const star = required ? <span className="text-red-600"> *</span> : null;

  if (layout === 'row') {
    return (
      <div className={`grid border-b ${BORDER} last:border-b-0 md:grid-cols-[300px_1fr] ${className}`}>
        <label htmlFor={id} className={`${TAN_LIGHT} px-4 py-2.5 text-[13px] font-semibold text-gray-800 md:border-r ${BORDER}`}>
          {label}
          {star}
        </label>
        <div className={`flex flex-col justify-center px-3 py-1 ${error ? 'bg-red-50/40' : ''}`}>
          {children}
          {msg && <p role="alert" className="pb-1 text-xs text-red-600">{msg}</p>}
        </div>
      </div>
    );
  }

  if (layout === 'bare') {
    return (
      <div className={className}>
        <label htmlFor={id} className="sr-only">{label}</label>
        {children}
        {msg && <p role="alert" className="mt-0.5 text-xs text-red-600">{msg}</p>}
      </div>
    );
  }

  if (layout === 'inline') {
    return (
      <div className={`md:flex md:items-end md:gap-3 ${className}`}>
        <label htmlFor={id} className="text-[13px] font-semibold text-gray-800 md:whitespace-nowrap md:pb-1.5">
          {label}
          {star}
        </label>
        <div className="min-w-0 flex-1">
          {children}
          {msg && <p role="alert" className="mt-1 text-xs text-red-600">{msg}</p>}
        </div>
      </div>
    );
  }

  return (
    <div className={className}>
      <label htmlFor={id} className="block text-[11px] font-semibold uppercase tracking-wider text-gray-500">
        {label}
        {star}
      </label>
      {children}
      {msg && <p role="alert" className="mt-1 text-xs text-red-600">{msg}</p>}
    </div>
  );
};

/**
 * layout: 'inline' (label left, underline)  | 'stacked' (small label above)
 *         'row' (label cell + value cell)   | 'bare' (input only, label is sr-only)
 */
export const Field = ({
  name, label, register, errors, rules, mask, type = 'text', layout = 'inline',
  className = '', placeholder, inputMode, maxLength, readOnly, autoComplete,
}) => {
  const r = register(name, rules);
  const error = getError(errors, name);
  const id = name.replace(/\./g, '-');
  const mk = mask ? MASKS[mask] : null;
  const numeric = ['int', 'ssn', 'ein', 'zip', 'aba', 'phone', 'dob', 'money'].includes(mask);

  return (
    <FieldShell layout={layout} id={id} label={label} required={!!(rules && rules.required)} error={error} className={className}>
      <input
        id={id}
        type={type}
        {...r}
        onChange={(e) => {
          if (mk) e.target.value = mk(e.target.value);
          r.onChange(e);
        }}
        placeholder={placeholder}
        inputMode={inputMode || (numeric ? 'numeric' : undefined)}
        maxLength={maxLength}
        readOnly={readOnly}
        autoComplete={autoComplete || 'off'}
        aria-invalid={!!error}
        className={controlClass(layout, !!error)}
      />
    </FieldShell>
  );
};

export const SelectField = ({
  name, label, register, errors, rules, options, layout = 'inline', className = '', placeholder = 'Select…',
}) => {
  const r = register(name, rules);
  const error = getError(errors, name);
  const id = name.replace(/\./g, '-');
  return (
    <FieldShell layout={layout} id={id} label={label} required={!!(rules && rules.required)} error={error} className={className}>
      <select id={id} {...r} aria-invalid={!!error} className={`${controlClass(layout, !!error)} cursor-pointer`}>
        <option value="">{placeholder}</option>
        {options.map(([value, text]) => (
          <option key={value} value={value}>{text}</option>
        ))}
      </select>
    </FieldShell>
  );
};

export const CheckBox = React.forwardRef(({ className = '', ...props }, ref) => (
  <input
    ref={ref}
    type="checkbox"
    className={`h-4 w-4 cursor-pointer rounded-sm border-gray-400 accent-blue-700 ${className}`}
    {...props}
  />
));
CheckBox.displayName = 'CheckBox';

/** Small bordered box used for the "INITIAL" column of the Word document */
export const InitialsField = ({ name, register, errors, label = 'Initials' }) => {
  const r = register(name, { required: REQ, validate: V.initials });
  const error = getError(errors, name);
  return (
    <div className="text-center">
      <input
        id={name.replace(/\./g, '-')}
        {...r}
        onChange={(e) => {
          e.target.value = MASKS.initials(e.target.value);
          r.onChange(e);
        }}
        maxLength={4}
        placeholder="AB"
        aria-label={label}
        autoComplete="off"
        className={`h-11 w-16 rounded border-2 bg-white text-center text-base font-semibold uppercase tracking-widest text-blue-900 outline-none placeholder-gray-300 focus:border-blue-600 focus:ring-0 ${
          error ? 'border-red-500 bg-red-50' : 'border-gray-400'
        }`}
      />
      <p className="mt-1 text-[10px] font-semibold uppercase tracking-wider text-gray-500">{label}</p>
      {error && <p role="alert" className="mt-0.5 text-[11px] text-red-600">{error.message || REQ}</p>}
    </div>
  );
};

/** Typed signature – rendered in a script face so it reads like a signature line */
export const SignatureField = ({ name, register, errors, label = 'Signature', className = '' }) => {
  const r = register(name, { required: REQ, minLength: { value: 2, message: 'Type your full name' } });
  const error = getError(errors, name);
  return (
    <FieldShell layout="inline" id={name.replace(/\./g, '-')} label={label} required error={error} className={className}>
      <input
        id={name.replace(/\./g, '-')}
        {...r}
        autoComplete="off"
        placeholder="Type your full name to sign"
        style={{ fontFamily: "'Brush Script MT','Segoe Script','Snell Roundhand',cursive" }}
        className={`${controlClass('inline', !!error)} text-xl text-blue-900 placeholder:font-sans placeholder:text-sm`}
      />
    </FieldShell>
  );
};